import { css, html, LitElement, nothing, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { AppContextController } from "ui-common/appContext";
import { isFan, minimalViableCreator } from "helpers/minimalViableProfile";
import { PreviewController } from "../../../apps/profile/src/services/preview";
import { featureFlag } from "../wow-feature/wow-feature";

import "@phosphor-icons/webcomponents/PhMagnifyingGlass";
import "@phosphor-icons/webcomponents/PhHouse";
import "@phosphor-icons/webcomponents/PhPlus";

import "../manage-create-launcher/manage-create-launcher";
import { getAssetUrl } from "helpers/assetHelpers";
import type { DirectiveResult } from "lit/async-directive.js";

export interface TabMenuLabels {
  feed?: DirectiveResult;
  explore?: DirectiveResult;
}

@customElement("tab-menu")
export class TabMenu extends LitElement {
  static override styles = css`
    nav {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      display: flex;
      background: var(--sds-color-background-default-default);
      border-top: 1px solid var(--sds-color-border-default-default);
      z-index: 100;
      max-width: 600px;
      margin: 0 auto;
      width: 100%;
    }

    .tab {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      padding: 12px 8px;
      background: none;
      border: none;
      cursor: pointer;
      color: var(--sds-color-text-default-tertiary);
      transition: color 0.2s ease;
      font: var(--sds-typography-weight-medium) var(--sds-typography-scale-01) var(--sds-typography-family-title);
    }

    .tab:hover {
      color: var(--sds-color-text-default-secondary);
    }

    .tab.active {
      color: var(--sds-color-text-brand-on-brand);
    }

    .creator-avatar {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      object-fit: cover;
    }
  `;

  @property({ type: Object })
  createManageOptions!: {
    header: DirectiveResult;
    links: {
      label: DirectiveResult;
      description: DirectiveResult;
      href: string;
      icon: TemplateResult;
    }[];
  };

  @property({ type: Object }) labels!: TabMenuLabels;

  private previewController = new PreviewController(this);
  private appContextController = new AppContextController(this);

  private handleTabClick = (tab: string) => {
    if (tab === "feed") {
      window.location.href = "/feed";
    }

    if (tab === "explore") {
      window.location.href = "/explore";
    }

    if (tab === "profile") {
      const userHandle = this.appContextController.appContext.currentUser?.handle;
      window.location.href = `/${userHandle}`;
    }
  };

  private get activeTab() {
    const path = window.location.pathname;

    if (path === "/feed") {
      return "feed";
    }

    if (path === "/explore") {
      return "explore";
    }

    const userHandle = this.appContextController.appContext.currentUser?.handle;

    if (path === `/${userHandle}` || path === `/${userHandle}/`) {
      return "profile";
    }

    return undefined;
  }

  override render() {
    if (this.previewController.isPreviewMode) return nothing;

    const user = this.appContextController.appContext.currentUser;

    if (!user) {
      return nothing;
    }

    return html`
      <nav>
        ${featureFlag(
          (isFan(user) || minimalViableCreator(user)) && (user.creatorsFollowing?.length ?? 0) > 0,
          html`
            <button
              class="tab ${this.activeTab === "feed" ? "active" : ""}"
              @click=${() => this.handleTabClick("feed")}
            >
              <ph-house size="24"></ph-house>
              <span>${this.labels.feed}</span>
            </button>
          `,
        )}
        ${featureFlag(
          isFan(user) || minimalViableCreator(user),
          html`
            <button
              class="tab ${this.activeTab === "explore" ? "active" : ""}"
              @click=${() => this.handleTabClick("explore")}
            >
              <ph-magnifying-glass size="24"></ph-magnifying-glass>
              <span>${this.labels.explore}</span>
            </button>
          `,
        )}
        ${featureFlag(
          minimalViableCreator(user),
          html`
            <manage-create-launcher .createManageOptions=${this.createManageOptions}></manage-create-launcher>
            <button
              class="tab ${this.activeTab === "profile" ? "active" : ""}"
              @click=${() => this.handleTabClick("profile")}
            >
              <img src=${getAssetUrl(user.avatarUrl ?? "")} alt="${user.name}" class="creator-avatar" />
              <span>Profile</span>
            </button>
          `,
        )}
      </nav>
    `;
  }
}
